import { useAuthStore } from "@/store/auth/auth.store";
import { appQueryClient } from "@/api";
import { invalidateScreenQueries } from "@/api/screen.api";
import { ScreenEvents, type PresenterUpdateStatusPayload } from "@exibra/shared";
import { io, type Socket } from "socket.io-client";

interface Message {
    type: string;
    data: unknown;
}

class WebSocketService {
    private socket: Socket | null = null;

    public connect(): Socket {
        if (!this.socket) {
            this.socket = io(this.getSocketUrl(), {
                query: { clientType: "client" },
                auth: (callback) => {
                    void useAuthStore
                        .getState()
                        .getToken()
                        .then(
                            (token) => callback({ token }),
                            () => callback({ token: null }),
                        );
                },
                reconnection: true,
                reconnectionAttempts: 5,
                reconnectionDelay: 1000,
                transports: ["websocket"],
            });

            this.setupEventListeners();
        }
        return this.socket;
    }

    public disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    private setupEventListeners() {
        if (!this.socket) return;

        this.socket.on("connect", () => {
            console.log("Connected to WebSocket server");
        });

        this.socket.on("disconnect", () => {
            console.log("Disconnected from WebSocket server");
        });

        this.socket.on("presenterMessage", (data) => {
            console.log("Received message from presenter:", data);
            // Handle presenter messages here
        });

        this.socket.on("error", (error) => {
            console.error("WebSocket error:", error);
        });

        this.socket.on(ScreenEvents.SCREEN_CONNECTED, (data) => {
            invalidateScreenQueries(appQueryClient, data.id);
        });

        this.socket.on(ScreenEvents.SCREEN_DISCONNECTED, (data) => {
            invalidateScreenQueries(appQueryClient, data.id);
        });

        this.socket.on(
            ScreenEvents.PRESENTER_UPDATE_STATUS,
            (data: PresenterUpdateStatusPayload) => {
                invalidateScreenQueries(appQueryClient, data.id);
            },
        );
    }

    /**
     * Sends a typed message through the active socket connection.
     */
    public sendMessage(message: Message) {
        if (this.socket && this.socket.connected) {
            this.socket.emit("message", message);
        } else {
            console.error("Socket is not connected");
        }
    }

    /**
     * Returns whether the WebSocket client is connected.
     */
    public isConnected(): boolean {
        return this.socket?.connected ?? false;
    }

    /**
     * Returns the current socket instance, if one has been created.
     */
    public getSocket(): Socket | null {
        return this.socket;
    }

    /**
     * Resolves the WebSocket origin from the configured API URL.
     */
    private getSocketUrl() {
        // Use process.env for Electron apps instead of import.meta.envt
        const apiUrl = import.meta.env.VITE_API_URL;

        const socketUrl = apiUrl!.replace("http", "ws").split("/api")[0];

        return socketUrl;
    }
}

// Create and export the singleton instance
export const websocketService = new WebSocketService();
